import React, { useEffect, useState } from 'react';
import { useNetwork } from '../../../utils/network';
import { track, trackError } from '../../../utils/track';
import './Leaderboard.css';

/* The weekly TCG leaderboards page: This Week + up to the last 9, side by side,
 * scroll right for older ones. Styled to match the in-game board (see the mock).
 *
 * All data comes from the MAIN member API — the portal only ever talks to this
 * server: /tcg/leaderboard/weeks + /tcg/leaderboard/weekly for the boards, and
 * /tcg/prize-pool for what each place pays.
 *
 * A CLOSED week shows what it actually paid, which comes back on the board itself
 * (paidTokens, from the payout ledger). Only THIS week uses the live prize pool,
 * because this week has not been paid yet and the pool is genuinely what it is
 * playing for. The two used to be the same number — today's ladder, painted across
 * every column — so changing the prizes rewrote what past winners were shown to
 * have won. */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Entry = {
  rank: number | string;
  username: string;
  weeklyPoints: number;
  weeklyWins: number;
  // what this player was actually paid for this week; null until the week is paid
  paidTokens?: number | null;
};
type Week = { weekKey: string; current: boolean; entries: Entry[] };

// 'YYYYMMDD' (the Melbourne week-start Friday) -> "24 Jul"
const fmtDate = (wk: string) => `${Number(wk.slice(6, 8))} ${MONTHS[Number(wk.slice(4, 6)) - 1] || ''}`;
const inc = (n: number) => n.toLocaleString('en-US');

const Crown = () => (
  <svg className="cup" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" fill="#ffcf40" />
    <rect x="5" y="19" width="14" height="2.2" rx="1" fill="#ffcf40" />
  </svg>
);

function WeekColumn({
  week,
  index,
  prizes
}: {
  week: Week;
  index: number;
  prizes: number[];
}) {
  const rows = week.entries || [];
  const champ = rows[0];
  // This Week / Last Week / Week of — index 0 is always the current week here.
  const tag = week.current ? 'This Week' : index === 1 ? 'Last Week' : 'Week of';
  const meta = week.current ? 'Ends Fri 12AM AEST' : '';

  /* What to put beside a name.
   *
   * A closed week reports what it actually paid, per player, and that is the only
   * thing it may show — if it paid nothing, nothing appears. Today's ladder is a
   * forecast, and a forecast has no business sitting next to a result from three
   * weeks ago.
   *
   * This week has not been paid, so its ladder IS what it is playing for. */
  const prizeFor = (row: Entry, rank: number): number | null => {
    if (row.paidTokens != null) return row.paidTokens;
    if (!week.current) return null;
    return rank <= prizes.length ? prizes[rank - 1] : null;
  };
  const topPrize = champ ? prizeFor(champ, 1) : null;

  return (
    <div className={`lb-week${week.current ? ' current' : ''}`}>
      <div className="lb-whead">
        <div className="lb-wtag">
          {week.current && <span className="live" />}
          {tag}
        </div>
        <div className="lb-wdate">{fmtDate(week.weekKey)}</div>
        {meta && <div className="lb-wmeta">{meta}</div>}
      </div>

      {champ && (
        <div className="lb-champ">
          <Crown />
          <div className="who">
            <div className="lab">{week.current ? 'Leading' : 'Winner'}</div>
            <div className="nm">{champ.username}</div>
            {topPrize != null && <div className="cwon">{inc(topPrize)} INC</div>}
          </div>
          <div className="pts">
            {champ.weeklyPoints}
            <small>PTS</small>
          </div>
        </div>
      )}

      <div className="lb-rows">
        {rows.length > 1 ? (
          rows.slice(1).map((r, i) => {
            const rank = i + 2; // rows[0] is the champion, so the list starts at 2
            const prize = prizeFor(r, rank);
            const cls = rank <= 5 ? ` p${rank}` : '';
            return (
              <div className={`lb-row${cls}${prize != null ? ' paid' : ''}`} key={`${week.weekKey}-${r.username}-${rank}`}>
                <span className="rk">{rank}</span>
                <span className="nm">
                  <span className="who">{r.username}</span>
                  {prize != null && <span className="won">{inc(prize)} INC</span>}
                </span>
                <span className="pt">{r.weeklyPoints}</span>
              </div>
            );
          })
        ) : rows.length === 0 ? (
          <div className="lb-empty">
            No ranked wins
            <br />
            this week
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { get } = useNetwork();
  const [weeks, setWeeks] = useState<Week[] | null>(null);
  const [prizes, setPrizes] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const railScrolled = React.useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Prize amounts — one per paying place (top N). Tolerate failure/unset:
        // the board still renders, just without the "won" line.
        try {
          const pp = await get('/tcg/prize-pool', true);
          if (alive && pp?.places) {
            setPrizes(
              pp.places
                .map((p: { rewardTokens?: string | number }) => Number(p.rewardTokens))
                .filter((n: number) => Number.isFinite(n))
            );
          }
        } catch {
          /* prizes are optional decoration */
        }

        const data = await get('/tcg/leaderboard/weeks?limit=10', true);
        const currentWeek: string = data.currentWeek;
        const listed: Array<{ weekKey: string; current: boolean }> = data.weeks || [];

        // Always lead with This Week, even before it has any scores (it won't be
        // in `weeks` until someone wins). Then the recent weeks, deduped, up to 10.
        const keys: string[] = [];
        const push = (k: string) => { if (k && !keys.includes(k)) keys.push(k); };
        push(currentWeek);
        listed.forEach((w) => push(w.weekKey));
        const chosen = keys.slice(0, 10);

        const boards = await Promise.all(
          chosen.map((k) =>
            get(`/tcg/leaderboard/weekly?week=${k}&limit=20`, true)
              .then((r: { entries?: Entry[] }) => r.entries || [])
              .catch(() => [] as Entry[])
          )
        );
        if (!alive) return;
        setWeeks(chosen.map((k, i) => ({ weekKey: k, current: k === currentWeek, entries: boards[i] })));
      } catch (e) {
        // Dead-end the admin should see — the board failed to load for this user.
        trackError('leaderboard.load', e);
        if (alive) setError('The leaderboards are unavailable right now. Please try again shortly.');
      }
    })();
    return () => {
      alive = false;
    };
    // once, on mount — get is recreated each render and we don't want to refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRailScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Fire once when the user scrolls the rail sideways to explore older weeks —
    // the one real interaction on this otherwise read-only page (engaged vs. bounced).
    if (railScrolled.current || e.currentTarget.scrollLeft <= 48) return;
    railScrolled.current = true;
    track('leaderboard.explore_weeks', { weeks: weeks?.length ?? 0 });
  };

  return (
    <div className="tcg-lb">
      <div className="lb-head">
        <div className="lb-kick">Trading Card Game</div>
        <h1 className="lb-title">
          Weekly <span className="cy">Leaderboards</span>
        </h1>
        <div className="lb-sub">
          <span>Top 20</span>
          {weeks && weeks.length > 1 && (
            <>
              <span className="dot" />
              <span className="lb-hint">
                Scroll for previous weeks <span className="arm">→</span>
              </span>
            </>
          )}
        </div>
      </div>

      {error ? (
        <div className="lb-state">{error}</div>
      ) : !weeks ? (
        <div className="lb-state">Loading leaderboards…</div>
      ) : (
        <div className="lb-rail" onScroll={onRailScroll}>
          {weeks.map((w, i) => (
            <WeekColumn key={w.weekKey} week={w} index={i} prizes={prizes} />
          ))}
        </div>
      )}
    </div>
  );
}
